import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Divider
} from '@mui/material';
import { Add, Home, ReceiptLong, ExpandMore, Replay } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { fmt, sym, locale } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(locale()) : '—';

const BatchReceiptList = () => {
  const navigate = useNavigate();
  const [batches,  setBatches]  = useState([]);
  const [error,    setError]    = useState('');
  const [detail,   setDetail]   = useState(null);
  const [, setLoading] = useState(false);
  const [success,  setSuccess]  = useState('');
  const [reverseBatch, setReverseBatch] = useState(null);
  const [busy,     setBusy]     = useState(false);

  const role = (JSON.parse(localStorage.getItem('user') || '{}')).role;
  const isAdmin = role === 'admin';

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchBatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/finance/batch-receipts`, { headers });
      setBatches(res.data);
    } catch { setError('Failed to load batch receipts'); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const openDetail = async (batchNo) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/finance/batch-receipts/${batchNo}`, { headers });
      setDetail(res.data);
    } catch { setError('Failed to load detail'); }
    finally { setLoading(false); }
  };

  const handleReverse = async () => {
    if (!reverseBatch) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/finance/reverse/batchreceipt/${reverseBatch.batchNo}`, {}, { headers });
      setSuccess(`Batch receipt ${reverseBatch.batchNo} reversed`);
      setReverseBatch(null);
      setDetail(null);
      fetchBatches();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reverse batch receipt');
      setReverseBatch(null);
    } finally { setBusy(false); }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <ReceiptLong color="primary" />
              <Typography variant="h5" color="primary">Batch Payment Receipts</Typography>
              <Chip label={batches.length} size="small" color="primary" sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Back to Finance">
                <IconButton onClick={() => navigate('/finance')}
                  sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />}
                onClick={() => navigate('/finance/batch-receipts/new')}>
                New Batch Receipt
              </Button>
            </Box>
          </Box>
          <Toast error={error} success={success} onClose={(k) => (k === 'error' ? setError('') : setSuccess(''))} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Batch No</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Bank Account</strong></TableCell>
                <TableCell><strong>Reference</strong></TableCell>
                <TableCell align="center"><strong>Learners</strong></TableCell>
                <TableCell align="right"><strong>Total Received</strong></TableCell>
                <TableCell align="right"><strong>Applied</strong></TableCell>
                <TableCell align="right"><strong>Credit Created</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No batch receipts yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : batches.map(b => (
                <TableRow key={b._id} hover>
                  <TableCell><strong>{b.batchNo}</strong></TableCell>
                  <TableCell>{fmtDate(b.date)}</TableCell>
                  <TableCell><Typography variant="caption" fontFamily="monospace">{b.bankAccountNo}</Typography></TableCell>
                  <TableCell>{b.reference || <em style={{ color: '#bbb' }}>—</em>}</TableCell>
                  <TableCell align="center">{b.lineCount}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(b.totalReceived)}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(b.totalApplied)}</TableCell>
                  <TableCell align="right" sx={{ color: b.totalCredit > 0 ? 'warning.main' : 'text.disabled' }}>
                    {b.totalCredit > 0 ? fmt(b.totalCredit) : '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={b.status || 'Posted'} color={b.status === 'Reversed' ? 'default' : 'success'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {isAdmin && b.status !== 'Reversed' && (
                      <Tooltip title="Reverse batch receipt">
                        <IconButton size="small" color="error" onClick={() => setReverseBatch(b)}>
                          <Replay fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View detail">
                      <IconButton size="small" onClick={() => openDetail(b.batchNo)}>
                        <ExpandMore fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <ReceiptLong color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{detail.batchNo}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtDate(detail.date)} · {detail.bankAccountNo}
                    {detail.reference ? ` · ${detail.reference}` : ''}
                  </Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={detail.status || 'Posted'} color={detail.status === 'Reversed' ? 'default' : 'success'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            {isAdmin && detail.status !== 'Reversed' && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center',
                px: 3, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Button size="small" color="error" startIcon={<Replay />}
                  onClick={() => { const b = detail; setDetail(null); setReverseBatch(b); }}>
                  Reverse Batch
                </Button>
              </Box>
            )}
            <DialogContent dividers>
              <Box display="flex" gap={3} mb={2} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">Learners</Typography>
                  <Typography variant="h6">{detail.lineCount}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Received</Typography>
                  <Typography variant="h6">{sym()} {fmt(detail.totalReceived)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Applied to Invoices</Typography>
                  <Typography variant="h6" color="success.main">{sym()} {fmt(detail.totalApplied)}</Typography>
                </Box>
                {detail.totalCredit > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Credit Created</Typography>
                    <Typography variant="h6" color="warning.main">{sym()} {fmt(detail.totalCredit)}</Typography>
                  </Box>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              {(detail.lines || []).map((line, idx) => (
                <Box key={idx} mb={2} p={1.5} bgcolor="grey.50" borderRadius={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="subtitle2">
                      {line.learnerAdmNo} — {line.learnerName || ''}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip label={`Received: ${sym()} ${fmt(line.amountReceived)}`} size="small" />
                      <Chip label={`Applied: ${sym()} ${fmt(line.amountApplied)}`} size="small" color="success" variant="outlined" />
                      {line.creditCreated > 0 && (
                        <Chip label={`Credit: ${sym()} ${fmt(line.creditCreated)}`} size="small" color="warning" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                  {line.allocations?.length > 0 && (
                    <Table size="small" sx={{ mt: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 0.5 }}><strong>Invoice</strong></TableCell>
                          <TableCell sx={{ py: 0.5 }}><strong>Charge</strong></TableCell>
                          <TableCell align="right" sx={{ py: 0.5 }}><strong>Applied ({sym()})</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {line.allocations.map((al, ai) => (
                          <TableRow key={ai}>
                            <TableCell sx={{ py: 0.3 }}>{al.invoiceNo}</TableCell>
                            <TableCell sx={{ py: 0.3 }}>
                              {al.chargeItemCode && (
                                <Chip label={al.chargeItemCode} size="small" sx={{ mr: 0.5 }} />
                              )}
                              {al.chargeItemName}
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.3 }}>{fmt(al.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetail(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* Reverse confirm dialog */}
      <Dialog open={Boolean(reverseBatch)} onClose={() => !busy && setReverseBatch(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reverse Batch Receipt {reverseBatch?.batchNo}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This unwinds the whole batch: every applied receipt is reversed (Dr Debtors / Cr Bank,
            restoring the invoices), and any surplus banked as learner credit is reversed
            (Dr Advance / Cr Bank). Total {sym()} {fmt(reverseBatch?.totalReceived)} will be taken
            back out of {reverseBatch?.bankAccountNo}. If a learner's surplus credit has already been
            applied elsewhere, reverse that first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseBatch(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReverse} disabled={busy}>
            {busy ? 'Reversing…' : 'Reverse Batch'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchReceiptList;
